/**
 * Alerts API Service
 *
 * TanStack Query hooks for admin alert management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { callFunction } from '@/services/firebase/functions';
import type {
  AlertHistoryResponse,
  GetAlertHistoryRequest,
  GetAlertRulesResponse,
  UpdateAlertRuleRequest,
  UpdateAlertRuleResponse,
  AcknowledgeAlertRequest,
  AcknowledgeAlertResponse,
} from '@/types';

// ─── Query Functions ─────────────────────────────────────────────────────────

const getAlertHistory = async (
  filters?: GetAlertHistoryRequest
): Promise<AlertHistoryResponse> => {
  return callFunction<GetAlertHistoryRequest | undefined, AlertHistoryResponse>(
    'getAlertHistory',
    filters
  );
};

const updateAlertRule = async (
  params: UpdateAlertRuleRequest
): Promise<UpdateAlertRuleResponse> => {
  return callFunction<UpdateAlertRuleRequest, UpdateAlertRuleResponse>(
    'updateAlertRule',
    params
  );
};

const acknowledgeAlert = async (
  params: AcknowledgeAlertRequest
): Promise<AcknowledgeAlertResponse> => {
  return callFunction<AcknowledgeAlertRequest, AcknowledgeAlertResponse>(
    'acknowledgeAlert',
    params
  );
};

const getAlertRules = async (): Promise<GetAlertRulesResponse> => {
  return callFunction<Record<string, never>, GetAlertRulesResponse>('getAlertRules', {});
};

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const alertQueryKeys = {
  all: ['alerts'] as const,
  history: (filters?: GetAlertHistoryRequest) =>
    [...alertQueryKeys.all, 'history', filters] as const,
  unacknowledged: () =>
    [...alertQueryKeys.all, 'unacknowledged'] as const,
  rules: () => [...alertQueryKeys.all, 'rules'] as const,
};

// ─── TanStack Query Hooks ────────────────────────────────────────────────────

/**
 * Hook for fetching alert rules
 */
export const useAlertRules = () => {
  return useQuery({
    queryKey: alertQueryKeys.rules(),
    queryFn: getAlertRules,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching alert history with optional filters
 */
export const useAlertHistory = (filters?: GetAlertHistoryRequest) => {
  return useQuery({
    queryKey: alertQueryKeys.history(filters),
    queryFn: () => getAlertHistory(filters),
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Hook for fetching unacknowledged alerts
 * Auto-refreshes every 60 seconds
 */
export const useUnacknowledgedAlerts = () => {
  return useQuery({
    queryKey: alertQueryKeys.unacknowledged(),
    queryFn: () =>
      getAlertHistory({ acknowledged: false, limit: 50 }),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // 60 seconds
  });
};

/**
 * Hook to update an alert rule (create, update, or delete)
 */
export const useUpdateAlertRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAlertRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertQueryKeys.all });
    },
  });
};

/**
 * Hook to acknowledge an alert
 */
export const useAcknowledgeAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acknowledgeAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertQueryKeys.all });
    },
  });
};
